/**
 * ⚒️ Blacksmith — recipe-gated, luck-stone-modulated forging.
 *
 * 4 tabs:
 *  • 🔨 Forge       — pick an owned recipe → choose stone (or buy w/ OD) → forge
 *  • 🎁 Bay         — items waiting to be claimed (24h auto-list timer)
 *  • 📜 Recipes     — owned + locked w/ OD-shop purchase
 *  • 🟧 Luck Stones — buy more stones with Omni Dollars
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { confirmDialog } from "@/components/ui/confirm-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ───────── Sound FX (synthesized — no asset files) ───────── */
let _audioCtx = null;
function getAudioCtx() {
  if (typeof window === "undefined") return null;
  if (!_audioCtx) {
    try { _audioCtx = new (window.AudioContext || window.webkitAudioContext)(); }
    catch { return null; }
  }
  if (_audioCtx.state === "suspended") _audioCtx.resume();
  return _audioCtx;
}

function playAnvilClang() {
  const ctx = getAudioCtx();
  if (!ctx) return;
  const now = ctx.currentTime;
  // Two-strike anvil: two metallic dings 80ms apart
  [0, 0.08].forEach((delay) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "triangle";
    osc.frequency.setValueAtTime(880, now + delay);
    osc.frequency.exponentialRampToValueAtTime(220, now + delay + 0.35);
    gain.gain.setValueAtTime(0.001, now + delay);
    gain.gain.exponentialRampToValueAtTime(0.4, now + delay + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + delay + 0.4);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now + delay);
    osc.stop(now + delay + 0.5);
  });
}

function playForgeFail() {
  const ctx = getAudioCtx();
  if (!ctx) return;
  const now = ctx.currentTime;
  // Sad descending "fwoomp"
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "sawtooth";
  osc.frequency.setValueAtTime(180, now);
  osc.frequency.exponentialRampToValueAtTime(50, now + 0.6);
  gain.gain.setValueAtTime(0.001, now);
  gain.gain.exponentialRampToValueAtTime(0.2, now + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.7);
  osc.connect(gain).connect(ctx.destination);
  osc.start(now);
  osc.stop(now + 0.7);
}

function fmt(secs) {
  if (secs == null || secs <= 0) return "—";
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function useTick() {
  const [t, setT] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setT((x) => x + 1), 1000);
    return () => clearInterval(id);
  }, []);
  return t;
}

export default function BlacksmithPage() {
  const { user, loading: authLoading } = useAuth();
  const [state, setState] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [tab, setTab] = useState("forge");
  const [busy, setBusy] = useState(false);
  const tick = useTick();

  const refresh = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([
        axios.get(`${API}/blacksmith/state`),
        axios.get(`${API}/blacksmith/recipes`),
      ]);
      setState(s.data);
      setRecipes(r.data?.recipes || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load Blacksmith.");
    }
  }, []);

  useEffect(() => {
    if (authLoading || !user) return;
    refresh();
  }, [authLoading, user, refresh]);

  if (authLoading) return null;
  if (!user) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-neutral-200">
        <Header />
        <div className="max-w-3xl mx-auto p-8 text-center text-neutral-400">
          🔒 Sign in to enter the Blacksmith.
        </div>
        <Footer />
      </div>
    );
  }
  if (!state) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-neutral-200">
        <Header />
        <div className="max-w-3xl mx-auto p-8 text-center text-neutral-500">Heating up the forge…</div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-neutral-200" data-testid="blacksmith-page">
      <Header />
      <div className="max-w-6xl mx-auto px-4 py-6 pb-24">
        {/* Hero */}
        <div className="mb-6 rounded-2xl border border-orange-500/30 bg-gradient-to-br from-[#1a0d05] via-[#0A0A0A] to-[#1a0820] p-5 shadow-[0_0_30px_rgba(234,88,12,0.2)]">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-3xl font-black tracking-wide text-orange-400" style={{ fontFamily: 'Anton, sans-serif' }}>
                ⚒️ THE BLACKSMITH
              </div>
              <div className="text-xs text-neutral-400 mt-1">
                Forge gear from Farm materials · Base 10% success · Add a Luck Stone or buy one with 🪙 OD
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Pill icon="🪙" label="Chits" value={state.chits} testid="bs-wallet-chits" />
              <Pill icon="🪙" label="OD" value={state.omni_dollars} testid="bs-wallet-od" />
              <Pill icon="📜" label="Recipes" value={state.recipes_owned?.length || 0} testid="bs-recipes-count" />
              <Pill icon="🎁" label="In Bay" value={state.holding_bay?.length || 0} testid="bs-bay-count" />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-5 border-b border-neutral-800 overflow-x-auto">
          <TabBtn label="🔨 Forge"       active={tab === "forge"}   onClick={() => setTab("forge")}   testid="bs-tab-forge" />
          <TabBtn label={`🎁 Bay${(state.holding_bay?.length || 0) ? ` (${state.holding_bay.length})` : ""}`}
                  active={tab === "bay"}     onClick={() => setTab("bay")}     testid="bs-tab-bay" />
          <TabBtn label="📜 Recipes"     active={tab === "recipes"} onClick={() => setTab("recipes")} testid="bs-tab-recipes" />
          <TabBtn label="🟧 Luck Stones" active={tab === "stones"}  onClick={() => setTab("stones")}  testid="bs-tab-stones" />
        </div>

        {tab === "forge" && <ForgeTab state={state} busy={busy} setBusy={setBusy} refresh={refresh} />}
        {tab === "bay" && <BayTab state={state} tick={tick} busy={busy} setBusy={setBusy} refresh={refresh} />}
        {tab === "recipes" && <RecipesTab recipes={recipes} busy={busy} setBusy={setBusy} refresh={refresh} />}
        {tab === "stones" && <StonesTab state={state} busy={busy} setBusy={setBusy} refresh={refresh} />}
      </div>
      <Footer />
    </div>
  );
}

/* ───────── Building blocks ───────── */
function Pill({ icon, label, value, testid }) {
  return (
    <div className="px-3 py-1.5 rounded-full bg-black/40 border border-neutral-700 text-sm flex items-center gap-1.5" data-testid={testid}>
      <span>{icon}</span>
      <span className="font-bold text-orange-300">{value}</span>
      <span className="text-neutral-500 text-xs">{label}</span>
    </div>
  );
}

function TabBtn({ label, active, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
            className={`px-4 py-2 text-sm font-bold border-b-2 transition whitespace-nowrap ${
              active ? "border-orange-400 text-orange-200" : "border-transparent text-neutral-500 hover:text-neutral-300"
            }`}>
      {label}
    </button>
  );
}

/* ───────── Forge Tab ───────── */
function ForgeTab({ state, busy, setBusy, refresh }) {
  const owned = state.owned_recipes_full || [];
  const [selectedRecipe, setSelectedRecipe] = useState(owned[0]?.recipe_id || null);
  const [stoneLevel, setStoneLevel] = useState(0); // 0 = no stone
  const [buyWithOD] = useState(false);   // legacy — mid-forge purchase removed Feb 2026 v2
  // Animation state — 'idle' | 'forging' | 'success' | 'fail'
  const [forgeAnim, setForgeAnim] = useState("idle");
  const animTimer = useRef(null);

  useEffect(() => {
    if (!selectedRecipe && owned.length) setSelectedRecipe(owned[0].recipe_id);
  }, [owned, selectedRecipe]);

  if (owned.length === 0) {
    return (
      <div className="text-center py-12 text-neutral-500" data-testid="forge-no-recipes">
        <div className="text-5xl mb-3">📜</div>
        You don't own any recipes yet. Head to the 📜 Recipes tab or earn them via Cave drops, Boss raids, and 🍳 Dave's Kitchen.
      </div>
    );
  }

  const recipe = owned.find((r) => r.recipe_id === selectedRecipe) || owned[0];
  const stoneInfo = stoneLevel ? state.stones_catalog?.[stoneLevel] : null;
  const successPct = Math.min(state.constants.max_success_pct,
                              state.constants.base_success_pct + (stoneInfo?.bump_pct || 0));
  const chitsFee = state.constants.chits_fee_by_rarity?.[recipe.rarity] || 10;
  const chitsForStone = (buyWithOD && stoneInfo) ? (stoneInfo.chits_cost ?? stoneInfo.od_cost) : 0;
  const ownedStones = state.luck_stones || {};
  const haveStone = stoneLevel ? Number(ownedStones[`lv${stoneLevel}`] || 0) > 0 : true;

  const onForge = async () => {
    if (busy) return;
    // 🧾 Roc #38 — confirm forge (materials + chits always burn, success rate shown).
    const okConfirm = await confirmDialog({
      title: `Forge ${recipe.name?.replace("Recipe — ", "")}?`,
      message: `${recipe.icon} ${recipe.rarity} ${recipe.slot}
Success chance: ${successPct}%  (base ${state.constants.base_success_pct}%${stoneInfo ? ` + ${stoneInfo.icon} Lv${stoneLevel} +${stoneInfo.bump_pct}%` : ""})
Cost: ${chitsFee} 🪙 Chits + all listed materials${stoneLevel ? ` + 1× Lv${stoneLevel} Luck Stone` : ""}.
⚠️ ON FAILURE: Chits + materials${stoneLevel ? " + stone" : ""} are BURNED. Recipe stays unlocked.`,
      confirmLabel: `Forge · ${successPct}% chance`,
      cancelLabel: "Not yet",
    });
    if (!okConfirm) return;
    setBusy(true);
    setForgeAnim("forging");
    playAnvilClang();
    try {
      const body = { recipe_id: recipe.recipe_id };
      if (stoneLevel) {
        body.stone_level = stoneLevel;
        if (!haveStone) body.buy_stone_with_od = true;
      }
      const r = await axios.post(`${API}/blacksmith/forge`, body);
      if (r.data.success) {
        setForgeAnim("success");
        // Second clang on success for the "sealed it" feel
        setTimeout(() => playAnvilClang(), 250);
        toast.success(`✨ FORGED ${r.data.icon || ""} ${r.data.item_name}! (rolled ${r.data.rolled}/${r.data.success_pct}%) — claim from 🎁 Bay`, { duration: 6000 });
      } else {
        setForgeAnim("fail");
        playForgeFail();
        toast.error(`💥 FORGE FAILED (rolled ${r.data.rolled}, needed ≤${r.data.success_pct}). Materials & stone burned.`, { duration: 6000 });
      }
      refresh();
    } catch (e) {
      setForgeAnim("fail");
      playForgeFail();
      toast.error(e?.response?.data?.detail || "Forge failed.");
    } finally {
      setBusy(false);
      if (animTimer.current) clearTimeout(animTimer.current);
      animTimer.current = setTimeout(() => setForgeAnim("idle"), 2200);
    }
  };

  return (
    <div className="grid md:grid-cols-2 gap-4">
      {/* Recipe picker */}
      <div className="rounded-xl border border-neutral-800 bg-black/40 p-4">
        <div className="text-xs uppercase tracking-wider text-neutral-500 mb-2">Recipe</div>
        <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
          {owned.map((r) => (
            <button key={r.recipe_id} data-testid={`forge-recipe-${r.recipe_id}`}
                    onClick={() => setSelectedRecipe(r.recipe_id)}
                    className={`w-full text-left px-3 py-2 rounded-lg border transition ${
                      selectedRecipe === r.recipe_id
                        ? "border-orange-400 bg-orange-500/10"
                        : "border-neutral-800 hover:border-neutral-600"
                    }`}>
              <div className="flex items-center gap-2">
                <span className="text-xl">{r.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm truncate">{r.name?.replace("Recipe — ", "")}</div>
                  <div className="text-[10px] text-neutral-500 uppercase">{r.rarity} · {r.slot}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Forge controls */}
      <div className={`rounded-xl border-2 bg-gradient-to-br from-black/60 to-[#1a0a05] p-4 transition-all ${
        forgeAnim === "forging" ? "border-orange-300 shadow-[0_0_60px_rgba(251,146,60,0.6)] animate-pulse" :
        forgeAnim === "success" ? "border-emerald-400 shadow-[0_0_50px_rgba(52,211,153,0.7)]" :
        forgeAnim === "fail"    ? "border-red-500 shadow-[0_0_40px_rgba(239,68,68,0.6)]" :
        "border-orange-500/30"
      }`}>
        {/* Animation overlay */}
        {forgeAnim === "forging" && (
          <div className="text-center text-4xl animate-bounce mb-2" data-testid="forge-anim-working">
            🔨💥🔨💥
          </div>
        )}
        {forgeAnim === "success" && (
          <div className="text-center mb-2 animate-bounce" data-testid="forge-anim-success">
            <div className="text-5xl">✨🌟✨</div>
            <div className="text-xs text-emerald-300 font-black tracking-widest mt-1">SUCCESS!</div>
          </div>
        )}
        {forgeAnim === "fail" && (
          <div className="text-center mb-2" data-testid="forge-anim-fail">
            <div className="text-5xl grayscale opacity-60">💨💥💨</div>
            <div className="text-xs text-red-400 font-black tracking-widest mt-1">SHATTERED</div>
          </div>
        )}
        <div className="text-center mb-3">
          <div className="text-6xl">{recipe.icon}</div>
          <div className="font-bold text-orange-200 mt-1">{recipe.name?.replace("Recipe — ", "")}</div>
          <div className="text-xs text-neutral-500 uppercase tracking-wider">{recipe.rarity} · {recipe.slot}</div>
        </div>

        {/* Materials */}
        <div className="mb-3">
          <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">Materials needed</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(recipe.materials || {}).map(([k, v]) => (
              <span key={k} className="px-2 py-1 rounded bg-black/40 border border-neutral-700 text-xs">
                {v}× {k}
              </span>
            ))}
            <span className="px-2 py-1 rounded bg-amber-500/10 border border-amber-500/40 text-xs text-amber-200">
              🪙 {chitsFee} Chits
            </span>
          </div>
        </div>

        {/* Luck stone selector */}
        <div className="mb-3">
          <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">Luck Stone (optional)</div>
          <div className="grid grid-cols-3 gap-1.5">
            <button data-testid="forge-stone-0"
                    onClick={() => setStoneLevel(0)}
                    className={`px-2 py-2 rounded text-xs ${stoneLevel === 0 ? "bg-orange-500/20 border border-orange-400" : "bg-black/40 border border-neutral-800"}`}>
              No Stone<br /><span className="text-neutral-500">+0%</span>
            </button>
            {Object.values(state.stones_catalog || {}).map((s) => {
              const have = Number(ownedStones[`lv${s.level}`] || 0);
              const heroLvl = Number(state.hero_level || 1);
              const reqLvl = Number(s.required_hero_level || 0);
              const lvlLocked = reqLvl > heroLvl;
              const arcLocked = !!s.arc_locked;
              const locked = lvlLocked || arcLocked;
              const lockLabel = arcLocked ? "🔒 arc" : (lvlLocked ? `🔒 Lv${reqLvl}` : "");
              return (
                <button key={s.level} data-testid={`forge-stone-${s.level}`}
                        onClick={() => !locked && setStoneLevel(s.level)}
                        disabled={locked}
                        title={arcLocked ? (s.arc_unlock_hint || "Arc-locked") : (lvlLocked ? `Needs hero Lv ${reqLvl}` : "")}
                        className={`px-2 py-2 rounded text-xs transition ${
                          locked
                            ? "bg-neutral-950/60 border border-neutral-900 text-neutral-600 cursor-not-allowed"
                            : stoneLevel === s.level
                              ? "bg-orange-500/20 border border-orange-400"
                              : "bg-black/40 border border-neutral-800"
                        }`}>
                  {s.icon} Lv{s.level}<br />
                  <span className={locked ? "text-neutral-600" : "text-orange-300"}>+{s.bump_pct}%</span>
                  {locked ? (
                    <span className="block text-[9px] text-amber-500/70 mt-0.5">{lockLabel}</span>
                  ) : (
                    <span className="text-neutral-500 ml-1">x{have}</span>
                  )}
                </button>
              );
            })}
          </div>
          {stoneLevel > 0 && !haveStone && (
            <div className="mt-2 text-xs rounded-lg bg-zinc-900/60 border border-zinc-700/50 p-2 text-zinc-300"
                 data-testid="forge-no-stone-hint">
              <div className="font-bold text-amber-200">You don't own a Lv {stoneLevel} stone</div>
              <div className="text-[10px] text-zinc-400 mt-0.5">
                {stoneLevel === 1
                  ? "Lv 1 stones drop from Lv 1-5 campaign monsters. Hunt some, or pick a different level."
                  : `Head to the Stones tab to synthesize one from ${stoneInfo.synth_input_qty}× Lv ${stoneLevel - 1} stones + ${stoneInfo.synth_chits_cost?.toLocaleString()} 🪙.`}
              </div>
            </div>
          )}
        </div>

        {/* Success rate strip */}
        <div className="mb-3 p-3 rounded-lg bg-black/60 border border-neutral-800 text-center">
          <div className="text-[10px] uppercase text-neutral-500 tracking-wider">Success Chance</div>
          <div className="text-3xl font-black text-orange-300" data-testid="forge-success-pct">{successPct}%</div>
          <div className="h-2 rounded-full bg-neutral-900 overflow-hidden mt-1">
            <div className="h-full bg-gradient-to-r from-red-500 via-orange-500 to-emerald-500"
                 style={{ width: `${successPct}%` }} />
          </div>
        </div>

        <button data-testid="forge-btn"
                disabled={busy || (stoneLevel > 0 && !haveStone)}
                onClick={onForge}
                className={`w-full px-4 py-3 rounded-lg bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 disabled:opacity-50 text-white font-black tracking-wider transition-all ${
                  forgeAnim === "forging" ? "scale-95 ring-4 ring-orange-300" :
                  forgeAnim === "success" ? "from-emerald-600 to-amber-500" :
                  forgeAnim === "fail" ? "from-red-800 to-neutral-700" : ""
                }`}>
          {forgeAnim === "forging" ? "🔥 FORGING…" :
           forgeAnim === "success" ? "✨ FORGED" :
           forgeAnim === "fail"    ? "💥 SHATTERED" :
           "🔨 FORGE"}
        </button>
        <div className="text-[10px] text-center text-neutral-500 mt-2">
          ⚠️ On fail: Chits + materials + stone are burned. Recipe stays unlocked.
        </div>
      </div>
    </div>
  );
}

/* ───────── Bay Tab ───────── */
function BayTab({ state, tick, busy, setBusy, refresh }) {
  const bay = state.holding_bay || [];
  const onClaim = async (bay_id) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/blacksmith/claim`, { bay_id });
      toast.success(`🎁 Claimed ${r.data.item_id}!`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Claim failed.");
    } finally {
      setBusy(false);
    }
  };
  if (bay.length === 0) {
    return (
      <div className="text-center py-12 text-neutral-500" data-testid="bay-empty">
        <div className="text-5xl mb-3">🎁</div>
        Holding bay is empty. Forge something at the 🔨 Forge tab.
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="bay-list">
      {bay.map((b) => {
        const left = Math.max(0, (b.seconds_to_autolist || 0) - tick);
        const urgent = left < 3600;
        return (
          <div key={b.bay_id}
               className={`rounded-xl border p-3 ${urgent ? "border-red-500/60 bg-red-950/20" : "border-neutral-800 bg-black/40"}`}
               data-testid={`bay-item-${b.bay_id}`}>
            <div className="text-center text-5xl mb-2">{b.icon || "📦"}</div>
            <div className="text-center font-bold text-orange-200 text-sm">{b.item_name}</div>
            <div className="text-center text-[10px] text-neutral-500 uppercase mb-2">{b.rarity} · {b.slot}</div>
            <div className={`text-center text-xs mb-2 ${urgent ? "text-red-300" : "text-amber-300"}`}>
              ⏰ Auto-lists in {fmt(left)}
            </div>
            <button data-testid={`bay-claim-${b.bay_id}`}
                    disabled={busy} onClick={() => onClaim(b.bay_id)}
                    className="w-full px-2 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm font-bold">
              🎁 Claim
            </button>
          </div>
        );
      })}
    </div>
  );
}

/* ───────── Recipes Tab ───────── */
function RecipesTab({ recipes, busy, setBusy, refresh }) {
  const onBuy = async (recipe) => {
    if (busy) return;
    // 🧾 Roc #38 — confirm recipe unlock.
    const ok = await confirmDialog({
      title: `Unlock recipe?`,
      message: `${recipe.icon} ${recipe.name?.replace("Recipe — ", "")}
Cost: ${recipe.od_shop_price} 🪙 OD (permanent unlock — unforged recipe stays yours).`,
      confirmLabel: `Buy — ${recipe.od_shop_price} OD`,
      cancelLabel: "Not yet",
    });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/blacksmith/buy-recipe`, { recipe_id: recipe.recipe_id });
      toast.success(`📜 Recipe unlocked! (-${r.data.od_spent} 🪙 OD)`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Buy failed.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="recipes-list">
      {recipes.map((r) => (
        <div key={r.recipe_id}
             className={`rounded-xl border p-3 ${r.owned ? "border-emerald-500/40 bg-emerald-950/10" : "border-neutral-800 bg-black/30"}`}
             data-testid={`recipe-${r.recipe_id}`}>
          <div className="flex items-start justify-between mb-2">
            <span className="text-4xl">{r.icon}</span>
            {r.owned ? (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200">OWNED</span>
            ) : (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-neutral-700 text-neutral-400">LOCKED</span>
            )}
          </div>
          <div className="font-bold text-sm">{r.name?.replace("Recipe — ", "")}</div>
          <div className="text-[10px] text-neutral-500 uppercase mb-2">{r.rarity} · {r.slot}</div>
          <div className="text-xs text-neutral-400">
            Materials: {Object.entries(r.materials || {}).map(([k, v]) => `${v}× ${k}`).join(" · ")}
          </div>
          {!r.owned && r.od_shop_price && (
            <button data-testid={`recipe-buy-${r.recipe_id}`}
                    disabled={busy} onClick={() => onBuy(r)}
                    className="w-full mt-2 px-2 py-1.5 rounded bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white text-sm font-bold">
              🪙 Buy for {r.od_shop_price} OD
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

/* ───────── Stones Tab — Synthesis Tree (Feb 2026 v2) ───────── */
function StonesTab({ state, busy, setBusy, refresh }) {
  const owned = state.luck_stones || {};
  const heroLvl = Number(state.hero_level || 1);
  const onSynth = async (target_level) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/blacksmith/synthesize-stone`, { target_level, qty: 1 });
      const s = state.stones_catalog[target_level];
      toast.success(
        `${s.icon} +1 Lv${target_level} synthesized! Burned ${r.data.inputs_burned}× Lv${target_level - 1} · -${r.data.chits_spent.toLocaleString()} 🪙`
      );
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Synthesis failed.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-3">
      <div className="rounded-lg bg-purple-950/40 border border-purple-700/40 p-3 text-xs text-purple-200">
        <div className="font-bold text-purple-100 mb-1">🪨 Stone Synthesis Tree</div>
        <div className="text-[11px] text-purple-300/80">
          Lv 1 stones drop from Lv 1-5 campaign monsters &amp; Wilds mobs.
          Lv 2-9 are synthesized: <span className="font-mono">2× Lv N + Chits → 1× Lv N+1</span>.
          Higher tiers unlock with story arcs; each tier also requires a minimum hero level to drop into a forge.
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="stones-list">
        {Object.values(state.stones_catalog || {})
          .sort((a, b) => a.level - b.level)
          .map((s) => {
            const have = Number(owned[`lv${s.level}`] || 0);
            const isLv1 = s.level === 1;
            const inputLv = s.level - 1;
            const inputsNeeded = s.synth_input_qty || 2;
            const inputsOwned = Number(owned[`lv${inputLv}`] || 0);
            const chitsCost = s.synth_chits_cost ?? 0;
            const arcLocked = !!s.arc_locked;
            const reqLvl = Number(s.required_hero_level || 0);
            const lvlLocked = reqLvl > heroLvl;
            const canSynth =
              !isLv1 &&
              !arcLocked &&
              inputsOwned >= inputsNeeded &&
              (state.chits ?? 0) >= chitsCost;
            return (
              <div
                key={s.level}
                className={`rounded-xl border p-3 text-center ${
                  arcLocked ? "border-amber-900/40 bg-amber-950/10 opacity-70" : "border-neutral-800 bg-black/40"
                }`}
                data-testid={`stone-card-${s.level}`}
              >
                <div className="text-4xl">{s.icon}{arcLocked ? " 🔒" : ""}</div>
                <div className="font-bold text-sm mt-1">Lv{s.level}</div>
                <div className="text-[10px] text-neutral-500">{s.label}</div>
                <div className={`text-lg font-bold mt-1 ${arcLocked ? "text-neutral-600" : "text-orange-300"}`}>+{s.bump_pct}%</div>
                <div className="text-[10px] text-amber-200 mt-1">You own: {have}</div>
                {reqLvl > 0 && (
                  <div className={`text-[9px] mt-0.5 ${lvlLocked ? "text-red-400" : "text-emerald-500/70"}`}
                       data-testid={`stone-lvreq-${s.level}`}>
                    Forge-use requires hero Lv {reqLvl}{lvlLocked ? ` (you're Lv ${heroLvl})` : " ✓"}
                  </div>
                )}
                {arcLocked ? (
                  <div
                    className="w-full mt-2 px-2 py-1.5 rounded bg-amber-950/50 border border-amber-800/60 text-amber-300 text-[10px] font-bold leading-tight"
                    data-testid={`stone-arc-locked-${s.level}`}
                  >
                    🔒 {s.arc_unlock_hint || "Unlocks with a future story arc."}
                  </div>
                ) : isLv1 ? (
                  <div
                    className="w-full mt-2 px-2 py-1.5 rounded bg-zinc-800 text-amber-300/80 text-[10px] font-bold leading-tight"
                    data-testid={`stone-drop-only-${s.level}`}
                  >
                    🎯 Drops from Lv 1-5 mobs
                  </div>
                ) : (
                  <>
                    <div className="text-[10px] text-neutral-400 mt-1">
                      {inputsNeeded}× Lv{inputLv} + {chitsCost.toLocaleString()} 🪙
                    </div>
                    <button
                      data-testid={`stone-synth-${s.level}`}
                      disabled={busy || !canSynth}
                      onClick={() => onSynth(s.level)}
                      className="w-full mt-2 px-2 py-1.5 rounded bg-purple-600 hover:bg-purple-500 disabled:opacity-40 text-white text-xs font-bold"
                    >
                      🪨 Synthesize
                    </button>
                  </>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
}
